import {
  Box,
  RoutineOccasionInlineStatusCircleRelay,
  TouchableBox,
} from "arkh";
import React, { useCallback } from "react";
import { Platform } from "react-native";
import { graphql, useFragment, useMutation } from "react-relay";

import { TeamScheduleInlineRoutineOccasionListItem_TeamScheduleInlineRoutineOccasion$key } from "./__generated__/TeamScheduleInlineRoutineOccasionListItem_TeamScheduleInlineRoutineOccasion.graphql";
import * as Haptics from "expo-haptics";
import { useNavigation } from "@react-navigation/native";
import { UserLoggedInNavigatorNavigationProp } from "../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { TeamScheduleInlineRoutineOccasionListItemMutation } from "./__generated__/TeamScheduleInlineRoutineOccasionListItemMutation.graphql";
import { TeamScheduleRoutineListItemContent } from "./TeamScheduleRoutineListItemContent";

import { TeamScheduleListItemScheduleRange } from "./TeamScheduleListItemScheduleRange/TeamScheduleListItemScheduleRange";
import { TeamScheduleRoutineOccasionListItemBadge } from "./TeamScheduleRoutineOccasionListItemBadge";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

export const TeamScheduleInlineRoutineOccasionListItem = React.memo(
  TeamScheduleInlineRoutineOccasionListItemComponent
);

function TeamScheduleInlineRoutineOccasionListItemComponent(props: {
  inlineRoutineOccasion: TeamScheduleInlineRoutineOccasionListItem_TeamScheduleInlineRoutineOccasion$key;
  itemType: "timeOfDay" | "allDay" | "WEEKLY" | "MONTHLY";
}) {
  const navigation =
    useNavigation<UserLoggedInNavigatorNavigationProp<"Team">>();
  const inlineRoutineOccasion = useFragment(
    graphql`
      fragment TeamScheduleInlineRoutineOccasionListItem_TeamScheduleInlineRoutineOccasion on TeamScheduleInlineRoutineOccasion {
        id
        startAt
        taskExecution {
          id
          task {
            id
          }
          sectionId
          routineOccasion {
            id
            store {
              myRole
            }
            sequenceNumber
            notDoneStatus
            completedTaskExecutionCount
            totalTaskExecutionCount
            status(calculateNoTasks: true, calculateNotCompletedStarted: true)
            ...RoutineOccasionStatusCircleInline_RoutineOccasion
            ...TeamScheduleRoutineListItemContent_RoutineOccasion
            ...TeamScheduleRoutineOccasionListItemBadgeNew_routineOccasion
            ...TeamScheduleListItemScheduleRange_routineOccasion
          }
          routine {
            ...TeamScheduleRoutineListItemContent_Routine
            id
          }
          checkValue
        }
      }
    `,
    props.inlineRoutineOccasion
  );

  const [save] = useMutation<TeamScheduleInlineRoutineOccasionListItemMutation>(
    graphql`
      mutation TeamScheduleInlineRoutineOccasionListItemMutation(
        $taskExecutionId: ID!
        $employeeId: ID
        $value: TaskSentiment
      ) @raw_response_type {
        setTaskExecutionCheck(
          id: $taskExecutionId
          employeeId: $employeeId
          sentiment: $value
        ) {
          id
          checkValue
          isCompleted
          routineOccasion {
            completedTaskExecutionCount
            totalTaskExecutionCount
            status(calculateNoTasks: true, calculateNotCompletedStarted: true)
          }
        }
      }
    `
  );

  const { currentEmployeeId } = usePersistedEmployeeId();
  const onSave = useCallback(() => {
    const nextVal =
      inlineRoutineOccasion.taskExecution.checkValue === "POSITIVE"
        ? null
        : "POSITIVE";

    save({
      variables: {
        taskExecutionId: inlineRoutineOccasion.taskExecution.id,
        employeeId: currentEmployeeId,
        value: nextVal,
      },
      optimisticResponse:
        nextVal === "POSITIVE"
          ? {
              setTaskExecutionCheck: {
                checkValue: nextVal,
                id: inlineRoutineOccasion.taskExecution.id,
                isCompleted: true,
                routineOccasion: {
                  id: inlineRoutineOccasion.taskExecution.routineOccasion.id,
                  completedTaskExecutionCount:
                    inlineRoutineOccasion.taskExecution.routineOccasion
                      .completedTaskExecutionCount,
                  totalTaskExecutionCount:
                    inlineRoutineOccasion.taskExecution.routineOccasion
                      .totalTaskExecutionCount,
                  status: "COMPLETED",
                },
              },
            }
          : {
              setTaskExecutionCheck: {
                checkValue: nextVal,
                id: inlineRoutineOccasion.taskExecution.id,
                isCompleted: false,
                routineOccasion: {
                  id: inlineRoutineOccasion.taskExecution.routineOccasion.id,
                  completedTaskExecutionCount:
                    inlineRoutineOccasion.taskExecution.routineOccasion
                      .completedTaskExecutionCount,
                  totalTaskExecutionCount:
                    inlineRoutineOccasion.taskExecution.routineOccasion
                      .totalTaskExecutionCount,
                  status:
                    inlineRoutineOccasion.taskExecution.routineOccasion
                      .notDoneStatus,
                },
              },
            },
    });
  }, [
    currentEmployeeId,
    inlineRoutineOccasion.taskExecution.checkValue,
    inlineRoutineOccasion.taskExecution.id,
    inlineRoutineOccasion.taskExecution.routineOccasion
      .completedTaskExecutionCount,
    inlineRoutineOccasion.taskExecution.routineOccasion.id,
    inlineRoutineOccasion.taskExecution.routineOccasion.notDoneStatus,
    inlineRoutineOccasion.taskExecution.routineOccasion.totalTaskExecutionCount,
    save,
  ]);

  const onPress = useCallback(() => {
    navigation.navigateDeprecated("RoutineOccasion", {
      routineId: inlineRoutineOccasion.taskExecution.routine!.id,
      sequenceNumber:
        inlineRoutineOccasion.taskExecution.routineOccasion.sequenceNumber,
      isNotFeedOrigin: false,
    });
  }, [
    inlineRoutineOccasion.taskExecution.routine,
    inlineRoutineOccasion.taskExecution.routineOccasion.sequenceNumber,
    navigation,
  ]);

  return (
    <TouchableBox
      spaceVertical="medium"
      spaceLeft="medium"
      onPress={onPress}
      style={{
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        height: "100%",
        justifyContent: "center",
      }}
    >
      <Box style={{ flexDirection: "column", flex: 1 }}>
        <TeamScheduleListItemScheduleRange
          itemType={props.itemType}
          routineOccasion={inlineRoutineOccasion.taskExecution.routineOccasion}
        />
        <TeamScheduleRoutineListItemContent
          routine={inlineRoutineOccasion.taskExecution.routine!}
          occasion={inlineRoutineOccasion.taskExecution.routineOccasion}
        />
      </Box>

      <TouchableBox
        onPressIn={() => {
          if (Platform.OS === "ios") {
            Haptics.impactAsync();
          }
        }}
        onPress={onSave}
        hitSlop={{ top: 25, bottom: 25, right: 30, left: 80 }}
        spaceHorizontal="medium"
        disabled={
          inlineRoutineOccasion.taskExecution.routineOccasion.store?.myRole ===
          "READ_ONLY"
        }
        style={{
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Box>
          <TeamScheduleRoutineOccasionListItemBadge
            occasion={inlineRoutineOccasion.taskExecution.routineOccasion}
          />
          <RoutineOccasionInlineStatusCircleRelay
            circleSize="medium"
            occasion={inlineRoutineOccasion.taskExecution.routineOccasion}
          />
        </Box>
      </TouchableBox>
    </TouchableBox>
  );
}
